import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Loader2, Save, Eye, EyeOff, X } from "lucide-react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { toast } from "react-toastify";
import { PASSWORD_POLICY_MESSAGE, validatePasswordPolicy } from "../../../utils/passwordPolicy";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  roleId: "",
  whatsAppPhone: "",
};

export default function StaffDrawer({
  open,
  mode,
  roles = [],
  initial,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";
  const topOffset = "var(--app-shell-topbar-size, 4rem)";
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const submitting = useRef(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    setShowPassword(false);

    if (isEdit && initial) {
      setForm({
        name: initial.name || "",
        email: initial.email || "",
        password: "",
        roleId: initial.roleId || "",
        whatsAppPhone: initial.whatsAppPhone || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [initial, isEdit, open]);

  const roleOptions = useMemo(
    () =>
      (roles || []).map(role => ({
        id: role.id,
        name: role.name,
      })),
    [roles]
  );

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const validate = () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.warn("Name must be at least 2 characters.");
      return false;
    }
    if (!isEdit) {
      if (!form.email || !String(form.email).includes("@")) {
        toast.warn("Enter a valid email.");
        return false;
      }
      const passwordError = validatePasswordPolicy(form.password);
      if (passwordError) {
        toast.warn(passwordError);
        return false;
      }
    }
    if (!form.roleId || !roleOptions.some(role => String(role.id) === String(form.roleId))) {
      toast.warn("Please select a role.");
      return false;
    }
    const phone = String(form.whatsAppPhone || "").trim();
    if (phone && /[A-Za-z]/.test(phone)) {
      toast.warn("Enter a valid WhatsApp phone number.");
      return false;
    }
    const digits = phone.replace(/\D/g, "");
    if (phone && (digits.length < 10 || digits.length > 15)) {
      toast.warn("Enter a valid WhatsApp phone number.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (submitting.current || !validate()) return;
    submitting.current = true;

    setSaving(true);
    try {
      if (!isEdit) {
        await onCreate({
          name: form.name.trim(),
          email: String(form.email).trim(),
          password: form.password,
          roleId: form.roleId,
          whatsAppPhone: String(form.whatsAppPhone || "").trim(),
        });
      } else {
        await onUpdate(initial.id, {
          name: form.name.trim(),
          roleId: form.roleId,
          whatsAppPhone: String(form.whatsAppPhone || "").trim(),
        });
      }
    } catch (failure) {
      toast.error(failure?.response?.data?.message || "Could not save team member. Your changes have been kept.");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={() => { if (!submitting.current) onClose(); }} className="fixed inset-x-0 bottom-0 z-[60]" style={{ top: topOffset }}>
      <div
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
        onClick={saving ? undefined : onClose}
      />

      <DialogPanel className="absolute right-0 top-0 h-full w-full border-l border-slate-200 bg-white sm:w-[480px]">
        <div className="flex h-full flex-col">
          <div className="relative overflow-hidden border-b border-slate-200 px-5 py-4">
            <div className="absolute inset-0 bg-slate-50" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                  {isEdit ? "Staff editor" : "Staff creation"}
                </p>
                <DialogTitle className="mt-1 text-base font-semibold text-slate-900">
                  {isEdit ? "Edit staff user" : "Create a user"}
                </DialogTitle>
                <p className="mt-0.5 text-[13px] text-slate-500">
                  {isEdit
                    ? "Update staff identity and role assignment."
                    : "Create a staff user and map them to an existing business role."}
                </p>
              </div>

              <button
                type="button"
                onClick={saving ? undefined : onClose}
                className="rounded-lg border border-slate-200 bg-white p-2 text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                disabled={saving}
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-white px-5 py-5 sm:px-6">
            <div className="mx-auto max-w-lg space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Identity</h3>
                <p className="border-none mt-0.5 text-[13px] text-slate-500">
                  Basic information and contact details.
                </p>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Name</label>
                    <input
                      aria-label="Name"
                      disabled={saving}
                      value={form.name}
                      onChange={event => set("name", event.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="e.g., Rahul Sharma"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-700">Email</label>
                    <input
                      aria-label="Email"
                      value={form.email}
                      onChange={event => set("email", event.target.value)}
                      disabled={isEdit || saving}
                      className={`mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 ${
                        isEdit ? "cursor-not-allowed opacity-70" : ""
                      }`}
                      placeholder="e.g., staff@company.com"
                    />
                    {isEdit ? (
                      <p className="mt-1.5 text-[12px] text-slate-500">
                        Email is locked for safety in the current flow.
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-700">
                      WhatsApp phone for team notifications
                    </label>
                    <input
                      aria-label="WhatsApp phone"
                      disabled={saving}
                      value={form.whatsAppPhone}
                      onChange={event => set("whatsAppPhone", event.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="e.g., 9876543210 or +919876543210"
                    />
                    <p className="mt-1.5 text-[12px] text-slate-500">
                      Used later for internal lead assignment notifications. This does not send messages yet.
                    </p>
                  </div>
                </div>
              </div>

              {!isEdit ? (
                <>
                  <hr className="border-slate-100" />
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Password</h3>
                    <p className="mt-0.5 text-[13px] text-slate-500">
                      Set a secure password for the new account.
                    </p>
                    <div className="mt-4">
                      <input
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        disabled={saving}
                        aria-label="Password"
                        value={form.password}
                        onChange={event => set("password", event.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="Use a strong password"
                      />
                      <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(value => !value)} className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600">{showPassword ? <EyeOff size={14}/> : <Eye size={14}/>}{showPassword ? "Hide password" : "Show password"}</button>
                      <p className="mt-1.5 text-[12px] text-slate-500">
                        {PASSWORD_POLICY_MESSAGE}
                      </p>
                    </div>
                  </div>
                </>
              ) : null}

              <hr className="border-slate-100" />
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Role assignment</h3>
                <p className="mt-0.5 text-[13px] text-slate-500">
                  Select a business role to define access rights.
                </p>
                <div className="mt-4">
                  <select
                    aria-label="Role"
                    disabled={saving}
                    value={form.roleId}
                    onChange={event => set("roleId", event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                  >
                    <option value="">Select a role</option>
                    {roleOptions.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                {roleOptions.length === 0 ? (
                  <p className="mt-2.5 text-[12px] text-amber-700">
                    No assignable roles are available yet. Create roles first in Roles & Permissions.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white px-5 py-3">
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={saving ? undefined : onClose}
                disabled={saving}
                className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-[13px] font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-1.5 text-[13px] font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isEdit ? (
                  <Save className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {saving ? "Saving..." : isEdit ? "Save changes" : "Create staff"}
              </button>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
